import React, { useState, useEffect, useCallback } from 'react';
import { fetchDashboardStats, fetchAllCategoriesFlat, updateOfferCategory, backfillEmbeddings } from '../api/thamanService';
import { categoryTree } from '../utils/categoryTree';
import { createCategorizationPrompt } from '../utils/prompts';
import callGeminiAPI from '../api/geminiAPI';
import CategorySelector from '../components/CategorySelector';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selection state
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Manual mapping state (single category for all selected)
    const [manualCategory, setManualCategory] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // AI mapping state
    const [isAiMapping, setIsAiMapping] = useState(false);
    const [aiProgress, setAiProgress] = useState('');
    // AI suggestions: Map of offerId -> categoryPath string (editable by user before applying)
    const [aiSuggestions, setAiSuggestions] = useState(null);

    // Embedding generation state
    const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false);
    const [embeddingProgress, setEmbeddingProgress] = useState('');
    const [embeddingMessage, setEmbeddingMessage] = useState('');

    // Flat categories for ID lookup
    const [flatCategories, setFlatCategories] = useState([]);

    // Build the full sub-tree once (used by AI categorization)
    const fullSubTree = (() => {
        const tree = {};
        for (const [topLevel, subTree] of Object.entries(categoryTree)) {
            for (const [subCat, leaves] of Object.entries(subTree)) {
                tree[`${topLevel} > ${subCat}`] = leaves;
            }
        }
        return tree;
    })();

    const loadData = useCallback(async () => {
        try {
            setError(null);
            setIsLoading(true);
            const [data, cats] = await Promise.all([
                fetchDashboardStats(),
                fetchAllCategoriesFlat(),
            ]);
            setStats(data);
            setFlatCategories(cats);
        } catch (err) {
            setError('Failed to fetch dashboard stats. Make sure the API server is running.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // --- Selection handlers ---
    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (!stats) return;
        if (selectedIds.size === stats.unmapped_offers.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(stats.unmapped_offers.map(o => o.id)));
        }
    };

    const selectedOffers = stats?.unmapped_offers?.filter(o => selectedIds.has(o.id)) || [];

    // --- Resolve category path to DB id ---
    const resolveCategoryId = (categoryPath) => {
        if (!categoryPath) return null;
        const leafName = categoryPath.split(' > ').pop().trim();
        const found = flatCategories.find(c => c.english_name === leafName);
        return found?.id || null;
    };

    // --- Manual mapping (same category for all selected) ---
    const handleManualMap = async () => {
        if (selectedOffers.length === 0 || !manualCategory) return;
        const categoryId = resolveCategoryId(manualCategory);
        if (!categoryId) {
            setSaveMessage(`Could not find category "${manualCategory}" in the database.`);
            return;
        }

        setIsSaving(true);
        setSaveMessage('');
        let successCount = 0;
        for (const offer of selectedOffers) {
            try {
                await updateOfferCategory(offer.id, categoryId);
                successCount++;
            } catch (err) {
                console.error(`Failed to update offer ${offer.id}:`, err);
            }
        }
        setSaveMessage(`Updated ${successCount} of ${selectedOffers.length} offers.`);
        setIsSaving(false);
        setSelectedIds(new Set());
        setManualCategory('');
        await loadData();
    };

    // --- AI mapping: Step 1 — get suggestions (don't apply yet) ---
    const handleAiSuggest = async () => {
        if (selectedOffers.length === 0) return;

        setIsAiMapping(true);
        setAiProgress('Preparing offers for AI categorization...');
        setSaveMessage('');
        setAiSuggestions(null);

        try {
            const suggestions = {}; // offerId -> categoryPath
            const batchSize = 15;

            for (let i = 0; i < selectedOffers.length; i += batchSize) {
                const batch = selectedOffers.slice(i, i + batchSize);
                setAiProgress(`Processing batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(selectedOffers.length / batchSize)}...`);

                const prompt = createCategorizationPrompt(
                    batch.map(o => ({ english_name: o.english_name, vendor_english: '' })),
                    fullSubTree
                );

                try {
                    const dummyImage = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';
                    const aiResult = await callGeminiAPI(prompt, dummyImage);

                    if (Array.isArray(aiResult)) {
                        const subKeys = Object.keys(fullSubTree);
                        for (const [productCode, categoryCode] of aiResult) {
                            const productIndex = parseInt(productCode.replace('P', '')) - 1;
                            if (productIndex < 0 || productIndex >= batch.length) continue;

                            const match = categoryCode.match(/^S(\d+)-(\d+)$/);
                            if (!match) continue;

                            const subIndex = parseInt(match[1]) - 1;
                            const leafIndex = parseInt(match[2]) - 1;
                            if (subIndex < 0 || subIndex >= subKeys.length) continue;

                            const subKey = subKeys[subIndex];
                            const leaves = fullSubTree[subKey];
                            if (leafIndex < 0 || leafIndex >= leaves.length) continue;

                            const leafName = leaves[leafIndex];
                            const fullPath = `${subKey} > ${leafName}`;
                            suggestions[batch[productIndex].id] = fullPath;
                        }
                    }
                } catch (err) {
                    console.error('AI categorization batch failed:', err);
                    setAiProgress(`Batch failed: ${err.message}. Continuing...`);
                }
            }

            // Fill any offers that AI didn't return a result for with empty string
            for (const offer of selectedOffers) {
                if (!suggestions[offer.id]) {
                    suggestions[offer.id] = '';
                }
            }

            setAiSuggestions(suggestions);
            setAiProgress('');
        } catch (err) {
            setSaveMessage(`AI categorization failed: ${err.message}`);
            setAiProgress('');
        } finally {
            setIsAiMapping(false);
        }
    };

    // --- AI mapping: Step 2 — user edits a single suggestion ---
    const updateSuggestion = (offerId, newPath) => {
        setAiSuggestions(prev => ({ ...prev, [offerId]: newPath }));
    };

    // --- AI mapping: Step 3 — apply reviewed suggestions ---
    const handleApplySuggestions = async () => {
        if (!aiSuggestions) return;

        setIsSaving(true);
        setSaveMessage('');
        let successCount = 0;
        let totalToApply = 0;

        for (const [offerIdStr, categoryPath] of Object.entries(aiSuggestions)) {
            if (!categoryPath) continue; // skip offers with no category assigned
            totalToApply++;
            const categoryId = resolveCategoryId(categoryPath);
            if (!categoryId) continue;

            try {
                await updateOfferCategory(parseInt(offerIdStr), categoryId);
                successCount++;
            } catch (err) {
                console.error(`Failed to update offer ${offerIdStr}:`, err);
            }
        }

        setSaveMessage(`Applied ${successCount} of ${totalToApply} category assignments.`);
        setIsSaving(false);
        setAiSuggestions(null);
        setSelectedIds(new Set());
        await loadData();
    };

    // --- Discard suggestions ---
    const handleDiscardSuggestions = () => {
        setAiSuggestions(null);
        setSaveMessage('');
    };

    // --- Generate embeddings in batches ---
    const handleGenerateEmbeddings = async () => {
        const totalMissing = stats?.missing_embeddings ?? 0;
        if (totalMissing === 0) return;

        setIsGeneratingEmbeddings(true);
        setEmbeddingMessage('');
        let totalUpdated = 0;
        let remaining = totalMissing;
        const batchSize = 50;

        try {
            while (remaining > 0) {
                setEmbeddingProgress(`Generating embeddings... ${totalUpdated} done, ${remaining} remaining`);
                const result = await backfillEmbeddings(batchSize);
                totalUpdated += result.updated;
                remaining -= result.checked;
                // If nothing was checked this round, stop to avoid infinite loop
                if (result.checked === 0) break;
            }
            setEmbeddingMessage(`Successfully generated ${totalUpdated} embeddings.`);
            await loadData();
        } catch (err) {
            setEmbeddingMessage(`Embedding generation failed: ${err.message}`);
        } finally {
            setIsGeneratingEmbeddings(false);
            setEmbeddingProgress('');
        }
    };

    if (isLoading) return <Loader message="Loading dashboard..." />;
    if (error) return <ErrorMessage message={error} />;
    if (!stats) return null;

    const allSelected = stats.unmapped_offers.length > 0 && selectedIds.size === stats.unmapped_offers.length;

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Active Offers</p>
                    <p className="mt-2 text-4xl font-bold text-green-600">{stats.active_offers}</p>
                    <p className="mt-1 text-sm text-gray-400">Non-expired offers</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Stores</p>
                    <p className="mt-2 text-4xl font-bold text-blue-600">{stats.total_stores}</p>
                    <p className="mt-1 text-sm text-gray-400">Supermarkets onboarded</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Active Flyers</p>
                    <p className="mt-2 text-4xl font-bold text-purple-600">{stats.flyers.active}</p>
                    <p className="mt-1 text-sm text-gray-400">out of {stats.flyers.total} total</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Expired Flyers</p>
                    <p className="mt-2 text-4xl font-bold text-red-500">{stats.flyers.expired}</p>
                    <p className="mt-1 text-sm text-gray-400">out of {stats.flyers.total} total</p>
                </div>
            </div>

            {/* === Offers Per Store === */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h2 className="text-lg font-bold text-gray-800 mb-3">Offers Per Store</h2>
                {stats.offers_per_store && stats.offers_per_store.length > 0 ? (
                    <div className="space-y-3">
                        {stats.offers_per_store.map(store => {
                            const maxCount = stats.offers_per_store[0]?.offer_count || 1;
                            const pct = Math.round((store.offer_count / maxCount) * 100);
                            return (
                                <div key={store.id}>
                                    <div className="flex justify-between items-baseline mb-1">
                                        <span className="text-sm font-medium text-gray-800">
                                            {store.english_name}
                                            <span className="text-xs text-gray-400 ml-1" dir="rtl">{store.arabic_name}</span>
                                        </span>
                                        <span className="text-sm text-gray-600">
                                            <span className="font-bold">{store.offer_count}</span>
                                            <span className="text-xs text-gray-400 ml-1">({store.active_count} active)</span>
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="h-2 rounded-full bg-blue-500"
                                            style={{ width: `${pct}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-gray-400 text-sm">No store data available.</p>
                )}
            </div>

            {/* === Missing Embeddings Section === */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Missing Embeddings</h2>
                            <p className="text-sm text-gray-500">
                                Offers without vector embeddings are invisible to semantic search and the AI chatbot.
                            </p>
                        </div>
                        <span className={`text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                            (stats.missing_embeddings ?? 0) === 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-700'
                        }`}>
                            {stats.missing_embeddings ?? 0} / {stats.total_offers ?? '?'} offers
                        </span>
                    </div>
                    {stats.total_offers > 0 && (
                        <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>
                                    {Math.round(((stats.total_offers - (stats.missing_embeddings ?? 0)) / stats.total_offers) * 100)}% embedded
                                </span>
                                <span>
                                    {stats.missing_embeddings ?? 0} missing
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                    className="h-2.5 rounded-full"
                                    style={{
                                        width: `${Math.round(((stats.total_offers - (stats.missing_embeddings ?? 0)) / stats.total_offers) * 100)}%`,
                                        backgroundColor: (stats.missing_embeddings ?? 0) === 0 ? '#22c55e' : '#ef4444',
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action bar */}
                {(stats.missing_embeddings ?? 0) > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-orange-800">
                                    {stats.missing_embeddings} offer{stats.missing_embeddings !== 1 ? 's' : ''} need embeddings
                                </p>
                                <p className="text-xs text-orange-600 mt-0.5">
                                    Embeddings are generated using Gemini AI in batches of 50. This may take a few minutes for large numbers.
                                </p>
                            </div>
                            <button
                                onClick={handleGenerateEmbeddings}
                                disabled={isGeneratingEmbeddings}
                                className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap ml-4"
                            >
                                {isGeneratingEmbeddings ? (
                                    <>
                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Generating...
                                    </>
                                ) : (
                                    'Generate All Embeddings'
                                )}
                            </button>
                        </div>
                        {embeddingProgress && (
                            <p className="text-sm text-orange-700 mt-2">{embeddingProgress}</p>
                        )}
                    </div>
                )}

                {/* Status message */}
                {embeddingMessage && (
                    <div className={`text-sm font-medium mb-3 px-3 py-2 rounded ${
                        embeddingMessage.includes('fail') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                    }`}>
                        {embeddingMessage}
                    </div>
                )}

                {(stats.missing_embeddings ?? 0) === 0 ? (
                    <p className="text-center text-gray-400 py-6">All offers have embeddings!</p>
                ) : (
                    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '660px' }}>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offer</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arabic Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(stats.missing_embeddings_list || []).map(offer => (
                                    <tr key={offer.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-900">{offer.english_name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500" dir="rtl">{offer.arabic_name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{offer.store_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{offer.category_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {offer.current_price ? `${offer.current_price} SAR` : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* === Top Vendors === */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Top Vendors</h2>
                {stats.top_vendors && stats.top_vendors.length > 0 ? (
                    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '660px' }}>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arabic Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Offers</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coverage</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.top_vendors.map((vendor, i) => {
                                    const maxCount = stats.top_vendors[0]?.offers_count || 1;
                                    const pct = Math.round((vendor.offers_count / maxCount) * 100);
                                    return (
                                        <tr key={vendor.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{vendor.english_name}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500" dir="rtl">{vendor.arabic_name}</td>
                                            <td className="px-4 py-3 text-sm font-bold text-gray-800">{vendor.offers_count}</td>
                                            <td className="px-4 py-3 text-sm text-green-600">{vendor.active_count}</td>
                                            <td className="px-4 py-3" style={{ minWidth: '120px' }}>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className="h-2 rounded-full bg-indigo-500"
                                                        style={{ width: `${pct}%` }}
                                                    ></div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-400 text-sm">No vendor data available.</p>
                )}
            </div>

            {/* === Unmapped Categories Section (moved to bottom) === */}
            <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Unmapped Categories</h2>
                            <p className="text-sm text-gray-500">
                                Offers with no category or assigned to "General" / "Other" — need manual sorting.
                            </p>
                        </div>
                        <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                            {stats.unmapped_count ?? stats.unmapped_offers.length} / {stats.total_offers ?? '?'} offers
                        </span>
                    </div>
                    {stats.total_offers > 0 && (
                        <div className="mt-2">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>
                                    {Math.round(((stats.total_offers - (stats.unmapped_count ?? 0)) / stats.total_offers) * 100)}% categorized
                                </span>
                                <span>
                                    {stats.unmapped_count ?? 0} unmapped
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div
                                    className="h-2.5 rounded-full"
                                    style={{
                                        width: `${Math.round(((stats.total_offers - (stats.unmapped_count ?? 0)) / stats.total_offers) * 100)}%`,
                                        backgroundColor: (stats.unmapped_count ?? 0) === 0 ? '#22c55e' : (stats.unmapped_count / stats.total_offers) > 0.2 ? '#f59e0b' : '#3b82f6',
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* AI Review Panel */}
                {aiSuggestions && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-bold text-purple-800">
                                AI Category Suggestions — Review & Edit
                            </h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleDiscardSuggestions}
                                    disabled={isSaving}
                                    className="px-3 py-1.5 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Discard
                                </button>
                                <button
                                    onClick={handleApplySuggestions}
                                    disabled={isSaving}
                                    className="px-4 py-1.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isSaving ? (
                                        <>
                                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Applying...
                                        </>
                                    ) : (
                                        'Apply All'
                                    )}
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-purple-600 mb-3">
                            Review the AI-suggested categories below. You can change any of them before applying. Offers left blank will be skipped.
                        </p>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {selectedOffers.map(offer => (
                                <div key={offer.id} className="flex items-center gap-3 bg-white rounded-lg border border-purple-100 p-3">
                                    <div className="min-w-0 flex-shrink-0" style={{ width: '200px' }}>
                                        <p className="text-sm font-medium text-gray-900 truncate">{offer.english_name}</p>
                                        <p className="text-xs text-gray-500 truncate" dir="rtl">{offer.arabic_name}</p>
                                    </div>
                                    <span className="text-gray-400 flex-shrink-0">&rarr;</span>
                                    <div className="flex-grow min-w-0">
                                        <CategorySelector
                                            value={aiSuggestions[offer.id] || ''}
                                            onChange={(newPath) => updateSuggestion(offer.id, newPath)}
                                            categoryTree={categoryTree}
                                        />
                                    </div>
                                    {aiSuggestions[offer.id] ? (
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">&#10003;</span>
                                    ) : (
                                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs">?</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action bar */}
                {selectedIds.size > 0 && !aiSuggestions && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm font-semibold text-blue-800 mb-3">
                            {selectedIds.size} offer{selectedIds.size !== 1 ? 's' : ''} selected
                        </p>
                        <div className="flex items-end gap-3 mb-3">
                            <div className="flex-grow">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Assign same category to all selected</label>
                                <CategorySelector
                                    value={manualCategory}
                                    onChange={setManualCategory}
                                    categoryTree={categoryTree}
                                />
                            </div>
                            <button
                                onClick={handleManualMap}
                                disabled={!manualCategory || isSaving}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {isSaving ? 'Saving...' : 'Apply Category'}
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleAiSuggest}
                                disabled={isAiMapping || isSaving}
                                className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isAiMapping ? (
                                    <>
                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Categorizing...
                                    </>
                                ) : (
                                    'Auto-Categorize with AI'
                                )}
                            </button>
                            {aiProgress && (
                                <span className="text-sm text-purple-700">{aiProgress}</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Status messages */}
                {saveMessage && (
                    <div className={`text-sm font-medium mb-3 px-3 py-2 rounded ${
                        saveMessage.includes('fail') || saveMessage.includes('Could not')
                            ? 'bg-red-50 text-red-700'
                            : 'bg-green-50 text-green-700'
                    }`}>
                        {saveMessage}
                    </div>
                )}

                {stats.unmapped_offers.length === 0 ? (
                    <p className="text-center text-gray-400 py-6">All offers are properly categorized!</p>
                ) : (
                    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '660px' }}>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offer</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arabic Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stats.unmapped_offers.map(offer => (
                                    <tr
                                        key={offer.id}
                                        className={`hover:bg-gray-50 cursor-pointer ${selectedIds.has(offer.id) ? 'bg-blue-50' : ''}`}
                                        onClick={() => toggleSelect(offer.id)}
                                    >
                                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(offer.id)}
                                                onChange={() => toggleSelect(offer.id)}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900">{offer.english_name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500" dir="rtl">{offer.arabic_name}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500">{offer.store_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                offer.category_name
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                {offer.category_name || 'No Category'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {offer.current_price ? `${offer.current_price} SAR` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                            {offer.expire_date
                                                ? new Date(offer.expire_date).toLocaleDateString()
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
